import React, { createContext, useContext, useState, useEffect } from "react";
import type { SearchResult } from "../types/search";

type SearchContextType = {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  exclude: string;
  setExclude: React.Dispatch<React.SetStateAction<string>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  results: SearchResult[];
  setResults: React.Dispatch<React.SetStateAction<SearchResult[]>>;
  clearSearch: () => void;
};

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [query, setQuery] = useState("");
  const [exclude, setExclude] = useState("");
  const [page, setPage] = useState(1);
  const [results, setResults] = useState<SearchResult[]>([]);

  // Restore state from sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem("searchState");
    if (saved) {
      const parsed = JSON.parse(saved);
      setQuery(parsed.query || "");
      setExclude(parsed.exclude || "");
      setPage(parsed.page || 1);
      setResults(parsed.results || []);
    }
  }, []);

  // Save state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "searchState",
      JSON.stringify({ query, exclude, page, results })
    );
  }, [query, exclude, page, results]);

  const clearSearch = () => {
    setQuery("");
    setExclude("");
    setPage(1);
    setResults([]);
    sessionStorage.removeItem("searchState");
  };

  return (
    <SearchContext.Provider
      value={{
        query,
        setQuery,
        exclude,
        setExclude,
        page,
        setPage,
        results,
        setResults,
        clearSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export function useSearch() {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch must be used inside a SearchProvider");
  return ctx;
}
